// Main-thread AI client. Spawns a Worker and provides promise-based RPC.
// Falls back to in-process execution when Worker isn't available (e.g., tests).

import { AIClient, MoveAnalysis } from "./api";
import { Play } from "../engine/moves";
import { Position, Side } from "../engine/position";
import { Difficulty } from "./levels";
import { analyzeMove, pickMove, decideCube, decideTake } from "./engine";
import { CubeAction, TakeAction } from "./cubeDecision";

type Pending = {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
  /** Re-runs this request in-process if the worker dies before answering. */
  runLocal: () => Promise<unknown>;
};

export function createWorkerClient(): AIClient {
  let worker: Worker | null = null;
  try {
    worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
  } catch {
    worker = null;
  }

  if (!worker) return createMainThreadClient();

  let nextId = 1;
  const pending = new Map<number, Pending>();

  // The construction above only throws for *synchronous* failures. A worker can
  // construct fine and then fail to load its module — an old WebView without
  // module-worker support, a bad MIME type, a CSP block. Without onerror every
  // pending promise would hang forever and the AI would silently stop, so treat
  // any worker-level failure as permanent and degrade to in-process execution.
  let dead = false;
  let localClient: AIClient | null = null;
  const local = (): AIClient => (localClient ??= createMainThreadClient());

  const killWorker = (): void => {
    if (dead) return;
    dead = true;
    const inflight = [...pending.values()];
    pending.clear();
    for (const p of inflight) p.runLocal().then(p.resolve, p.reject);
  };
  worker.onerror = killWorker;
  worker.onmessageerror = killWorker;

  worker.onmessage = (ev: MessageEvent<{ id: number; type: string; [k: string]: unknown }>) => {
    const m = ev.data;
    const p = pending.get(m.id);
    if (!p) return;
    pending.delete(m.id);
    if (m.type === "error") p.reject(new Error(String(m.message)));
    else p.resolve(m);
  };

  // `runLocal` must resolve to the same shape the worker replies with, so both
  // paths flow through one set of response mappings below.
  const send = <T>(req: Record<string, unknown>, runLocal: () => Promise<T>): Promise<T> => {
    if (dead) return runLocal();
    return new Promise<T>((resolve, reject) => {
      const id = nextId++;
      pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        runLocal: runLocal as () => Promise<unknown>,
      });
      worker!.postMessage({ id, ...req });
    });
  };

  return {
    async pickMove(p: Position, legalPlays: Play[], difficulty: Difficulty): Promise<Play> {
      const r = await send<{ play: Play }>(
        { type: "pick", pos: serialize(p), legalPlays, difficulty },
        async () => ({ play: await local().pickMove(p, legalPlays, difficulty) }),
      );
      return r.play;
    },
    async analyze(p: Position, legalPlays: Play[], difficulty: Difficulty, awayUs?: number, awayThem?: number): Promise<MoveAnalysis> {
      const r = await send<{
        bestPlay: Play;
        bestEquity: number;
        pointsEquities?: number[];
        gameEquities?: number[];
        // Compat: a stale service-worker-cached worker bundle may still send
        // the old single `equities` (game equity) vector.
        equities?: number[];
      }>(
        { type: "analyze", pos: serialize(p), legalPlays, difficulty, awayUs, awayThem },
        () => local().analyze(p, legalPlays, difficulty, awayUs, awayThem),
      );
      const game = r.gameEquities ?? r.equities ?? [];
      const points = r.pointsEquities ?? game;
      return {
        bestPlay: r.bestPlay,
        // Enforce the MoveAnalysis invariant at the boundary: bestEquity is the
        // max of the vector it is diffed against. A worker that reports the two
        // from different metrics (the old protocol did, and a stale cached
        // worker bundle still can) otherwise makes played-move losses come out
        // negative — which clamps to a free 0 and flatters every rating.
        bestEquity: maxFinite(points, r.bestEquity),
        pointsEquities: points,
        gameEquities: game,
      };
    },
    async decideCube(p: Position, side: Side): Promise<CubeAction> {
      const r = await send<{ action: CubeAction }>(
        { type: "cube", pos: serialize(p), side },
        async () => ({ action: await local().decideCube(p, side) }),
      );
      return r.action;
    },
    async decideTake(p: Position, side: Side): Promise<TakeAction> {
      const r = await send<{ action: TakeAction }>(
        { type: "take", pos: serialize(p), side },
        async () => ({ action: await local().decideTake(p, side) }),
      );
      return r.action;
    },
  };
}

export function createMainThreadClient(): AIClient {
  return {
    async pickMove(p, legalPlays, difficulty) {
      // Yield to event loop so UI updates before computation
      await new Promise<void>((res) => setTimeout(res, 0));
      return pickMove(p, legalPlays, difficulty);
    },
    async analyze(p, legalPlays, difficulty, awayUs, awayThem) {
      await new Promise<void>((res) => setTimeout(res, 0));
      const a = analyzeMove(p, legalPlays, difficulty, awayUs, awayThem);
      return {
        bestPlay: a.bestPlay,
        bestEquity: maxFinite(a.pointsEquities, a.bestEquity),
        pointsEquities: a.pointsEquities,
        gameEquities: a.gameEquities,
      };
    },
    async decideCube(p, side) {
      await new Promise<void>((res) => setTimeout(res, 0));
      return decideCube(p, side);
    },
    async decideTake(p, side) {
      await new Promise<void>((res) => setTimeout(res, 0));
      return decideTake(p, side);
    },
  };
}

/** Largest finite entry of `xs`, or `fallback` when there is none. */
function maxFinite(xs: number[], fallback: number): number {
  let best = -Infinity;
  for (const x of xs) if (Number.isFinite(x) && x > best) best = x;
  return best === -Infinity ? fallback : best;
}

function serialize(p: Position): Position {
  // Position is structured-cloneable already; this is for clarity.
  return p;
}
